import { Target, Eye, Compass } from "lucide-react";
import { BRAND, IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, SectionHeading, Reveal } from "@/components/Section";
import { ValuesGrid } from "@/components/ValuesGrid";
import { Testimonials } from "@/components/Testimonials";
import { TalkCta } from "@/components/TalkCta";

const STEP_TILES = ["tile-sky corner-tr", "tile-sun corner-bl", "tile-blue corner-tr", "tile-peach corner-bl"];
const PILLARS = [
  { id: "our-mission", icon: Target, title: "Our Mission", key: "mission", tile: "tile-blue corner-tr", light: true },
  { id: "our-vision", icon: Eye, title: "Our Vision", key: "vision", tile: "tile-sun corner-bl", light: false },
  { id: "our-purpose", icon: Compass, title: "Our Purpose", key: "purpose", tile: "tile-sky corner-tr", light: false },
];

const LogoStory = () => {
  const t = useT();
  const { BRAND: brand } = useSite();
  return (
    <section className="py-16 lg:py-24">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-square max-w-md mx-auto bg-[var(--blue-tint)] flex items-center justify-center p-12" style={{ borderRadius: "0 96px 0 96px" }} data-testid="about-logo">
            <img src={BRAND.logoIcon} alt={`${BRAND.name} logo`} className="w-full h-full object-contain float" />
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:ps-8">
          <SectionHeading eyebrow={t("Our logo")} title={t("A heart built around the individual")} text={brand.logoStory} />
          <Reveal delay={0.1}><p className="mt-5 text-base md:text-lg text-[var(--body)] leading-relaxed">{t("Our focus is to support your wellbeing and independence while respecting your choices and the comfort of your home.")}</p></Reveal>
        </div>
      </div>
    </section>
  );
};

const Pillars = () => {
  const t = useT();
  const { MISSION } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue-tint)]" data-testid="pillars-section" id="mission">
      <div className="container-x">
        <SectionHeading eyebrow={t("Mission, vision & purpose")} title={t("Compassionate, dependable care that respects your choices")} />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div className={`${p.tile} p-8 h-full`} data-testid={`pillar-${p.id}`}>
                <span className={`w-12 h-12 rounded-md flex items-center justify-center ${p.light ? "bg-white/20 text-white" : "bg-white text-[var(--blue)]"}`}><p.icon size={22} /></span>
                <h3 className={`mt-6 text-2xl font-semibold ${p.light ? "!text-white" : "!text-[var(--ink)]"}`}>{t(p.title)}</h3>
                <p className={`mt-3 leading-relaxed ${p.light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{MISSION[p.key]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Aims = () => {
  const t = useT();
  const { AIMS } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="aims-section">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><SectionHeading eyebrow={t("Our aims")} title={t("What we work towards every day")} /></div>
        <ol className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-8">
          {AIMS.map((a, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08}>
              <li className="flex gap-5" data-testid={`aim-${i}`}>
                <span className="text-3xl font-bold text-[var(--coral)] leading-none">{String(i + 1).padStart(2, "0")}</span>
                <div><h3 className="text-lg font-semibold !text-[var(--blue)]">{a.title}</h3><p className="mt-1.5 text-[15px] text-[var(--body)] leading-relaxed">{a.text}</p></div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const Approach = () => {
  const t = useT();
  const { STEPS } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue-tint)]" data-testid="about-approach">
      <div className="container-x">
        <SectionHeading eyebrow={t("Our approach")} title={t("Good care starts with understanding you")} text={t("We work alongside you and, where you choose, your family or support network to agree on care that suits your everyday life. As your needs change, we review your support with you. Your voice remains central throughout.")} />
        <ol className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-5">
          {STEPS.map((st, i) => {
            const light = STEP_TILES[i].includes("tile-blue");
            return (
              <Reveal key={st.n} delay={i * 0.08} className="h-full">
                <li className={`${STEP_TILES[i]} p-7 h-full`}>
                  <span className={`text-sm font-semibold ${light ? "text-white/80" : "text-[var(--blue)]"}`}>{st.n}</span>
                  <h3 className={`mt-3 text-lg font-semibold ${light ? "!text-white" : "!text-[var(--ink)]"}`}>{st.title}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{st.text}</p>
                </li>
              </Reveal>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

export default function About() {
  const t = useT();
  return (
    <>
      <PageIntro eyebrow={t("About us")} title={t("Care built around compassion,")} accent={t("respect and reliability.")} text={t("We understand that every person has different needs, routines and goals. We take the time to listen and shape support around your preferences — whether you need assistance with everyday tasks, personal care or participation in your community.")} image={IMAGES.hero} />
      <LogoStory />
      <Pillars />
      <Aims />
      <section className="py-16 lg:py-24" data-testid="values-section">
        <div className="container-x">
          <SectionHeading eyebrow={t("Our values")} title={t("Six values, one promise: you come first")} />
          <div className="mt-10"><ValuesGrid /></div>
        </div>
      </section>
      <Approach />
      <Testimonials />
      <TalkCta sourcePage="about" />
    </>
  );
}
